import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { authService, AuthUser } from '../Services/authService';
import { initializeCsrf } from '../Services/axiosInstance';
import { isInNativeTab, requestReauthentication } from '../utils/nativeTab';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  signIn: (returnUrl?: string) => void;
  signOut: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
  });

  // Handle authError=reauth_required redirect from backend (silent OIDC failed)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('authError');

    if (authError === 'reauth_required' && isInNativeTab()) {
      const returnUrl = params.get('returnUrl') || '/dashboard';
      // Clean up URL params before requesting platform re-auth
      window.history.replaceState({}, '', returnUrl);
      requestReauthentication(returnUrl);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const status = await authService.getAuthStatus();

        setAuthState({
          user: status.user,
          isLoading: false,
          isAuthenticated: status.isAuthenticated,
          error: null,
        });

        if (status.isAuthenticated) {
          await initializeCsrf();
        }
      } catch (error) {
        console.error('[AuthProvider] Auth initialization failed:', error);
        setAuthState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: error instanceof Error ? error.message : 'Authentication error',
        });
      }
    };

    initAuth();
  }, []);

  const signIn = useCallback((returnUrl?: string): void => {
    authService.signIn(returnUrl);
  }, []);

  const signOut = useCallback((): void => {
    authService.signOut();
  }, []);

  const clearError = useCallback((): void => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  const contextValue = useMemo<AuthContextType>(() => ({
    ...authState,
    signIn,
    signOut,
    clearError,
  }), [authState, signIn, signOut, clearError]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
